/**
 * Same-thread message queue helpers
 * (PR feat/same-thread-queue-while-streaming)
 *
 * Miles directive (2026-05-09 23:44 UTC): "yes it should be allowed".
 *
 * When the user types a SECOND message on the SAME thread while the
 * first turn is still streaming, that second message must:
 *   1) render its user bubble immediately (so the user sees the input
 *      was accepted),
 *   2) get *queued* in the thread's pending-message queue (NOT sent to
 *      the model yet),
 *   3) fire as a new turn the moment the first turn finalizes,
 *   4) carry the FULL conversation history (including its own user
 *      bubble plus the now-completed first turn's reply).
 *
 * Pure helpers extracted so the queue invariants can be unit-tested
 * without mounting Firebase auth, the platform message service, and
 * the rest of the GlobalChat dependency graph.  Same testing pattern
 * as `threadIsolation.ts`.
 *
 * Behavior spec locked into tests:
 *   - FIFO per-thread (each thread serializes within itself).
 *   - Per-thread cap of MAX_QUEUE_DEPTH (default 5). Beyond the cap,
 *     the input is gated with a small "queued, please wait" hint.
 *   - Concurrent threads each have their own queue — same-thread
 *     queueing serializes within a thread but does NOT block
 *     other threads.
 *   - Aborting the in-flight turn FLUSHES the queue (each pending
 *     entry is marked aborted with a recoverable user-visible note).
 *   - Switching threads mid-queue does NOT pause the queue — the
 *     queue continues draining in the background until empty; the
 *     thread chip's spinner stays on.
 *   - Deleting a thread mid-queue cleanly drops every pending entry
 *     for that thread (and the in-flight turn is aborted by the
 *     existing thread-removal path).
 */

import type { ChatMessage, PendingMessage } from './types';

export type { PendingMessage };

/**
 * Default per-thread queue cap. Beyond this, the composer's input is
 * gated and a small "5 messages queued, please wait" hint renders. The
 * cap exists so a runaway client (auto-typer, paste loop) can't grow
 * the in-memory queue unboundedly.
 */
export const MAX_QUEUE_DEPTH = 5;

/**
 * The queue state.  Map of threadId → ordered list of PendingMessages.
 * Order is FIFO: index 0 is the head (next to fire).
 */
export interface QueueState {
  queues: Record<string, PendingMessage[]>;
}

export const emptyQueueState: QueueState = { queues: {} };

/** Push a new pending message at the tail of `threadId`'s queue. */
export function enqueueMessage(state: QueueState, msg: PendingMessage): QueueState {
  const tid = msg.threadId;
  const prior = state.queues[tid] || [];
  return {
    queues: {
      ...state.queues,
      [tid]: [...prior, msg],
    },
  };
}

/**
 * Pop the head of `threadId`'s queue. Returns the new state and
 * the popped head (or null if the queue is empty).
 *
 * Keeps the threadId key around with an empty array so consumers
 * iterating `Object.keys(state.queues)` see a stable shape; that's
 * cleaned up by `pruneEmpty`.
 */
export function dequeueHead(
  state: QueueState,
  threadId: string,
): { state: QueueState; head: PendingMessage | null } {
  const prior = state.queues[threadId] || [];
  if (prior.length === 0) return { state, head: null };
  const [head, ...rest] = prior;
  const nextQueues = { ...state.queues };
  if (rest.length === 0) {
    delete nextQueues[threadId];
  } else {
    nextQueues[threadId] = rest;
  }
  return { state: { queues: nextQueues }, head };
}

export function peekHead(state: QueueState, threadId: string): PendingMessage | null {
  const q = state.queues[threadId];
  return q && q.length > 0 ? q[0] : null;
}

export function getQueueDepth(state: QueueState, threadId: string): number {
  const q = state.queues[threadId];
  return q ? q.length : 0;
}

/** True when a new send on `threadId` should be blocked by the
 *  composer's input gate (queue at or above the cap). */
export function isThreadQueueFull(
  state: QueueState,
  threadId: string,
  max: number = MAX_QUEUE_DEPTH,
): boolean {
  return getQueueDepth(state, threadId) >= max;
}

/**
 * Mark every queued entry for `threadId` as `aborted` and return the
 * list of aborted messages so the caller can render the recoverable
 * "queued and not sent — first turn stopped" note in the thread's
 * history.  The queue is cleared.
 *
 * Miles's directive doesn't pin the abort semantics ("OR alternatively
 * the queue could continue executing"); we go with the safer
 * flush-on-abort policy because:
 *   a) it preserves the user's mental model that "stop" means stop
 *      EVERYTHING this thread is doing,
 *   b) the queued entries can be re-sent from the rendered bubble if
 *      the user actually wanted them to fire,
 *   c) it avoids the awkward state where the user clicks Stop and
 *      the assistant immediately starts replying to a queued message
 *      they thought they had cancelled.
 */
export function flushQueue(
  state: QueueState,
  threadId: string,
  note = 'queued and not sent — first turn stopped',
): { state: QueueState; aborted: PendingMessage[] } {
  const prior = state.queues[threadId] || [];
  if (prior.length === 0) return { state, aborted: [] };
  const aborted = prior.map(m => ({ ...m, status: 'aborted' as const, abortedNote: note }));
  const nextQueues = { ...state.queues };
  delete nextQueues[threadId];
  return { state: { queues: nextQueues }, aborted };
}

/** Drop every pending entry for a thread (e.g., thread deleted). */
export function removeThreadQueue(state: QueueState, threadId: string): QueueState {
  if (!(threadId in state.queues)) return state;
  const nextQueues = { ...state.queues };
  delete nextQueues[threadId];
  return { queues: nextQueues };
}

/** Sum of queued messages across every thread — used by the global
 *  "drain in progress on background threads" indicator. */
export function totalQueueDepth(state: QueueState): number {
  return Object.values(state.queues).reduce((sum, q) => sum + q.length, 0);
}

/**
 * Live-bundle verification marker.  Same pattern as
 * THREAD_ISO_DEBUG_TAG (PR #71/#82): a unique grep-able token so we
 * can confirm the new behaviour shipped to prod by curling the
 * deployed bundle and grepping for it.
 */
export const THREAD_QUEUE_DEBUG_TAG = '[threadQueue]';

/**
 * Convenience: build a PendingMessage from the rendered ChatMessage
 * the composer just added to history. Centralized so the call site
 * in `useGlobalChat` and the tests stay in sync on the field shape.
 */
export function pendingFromChatMessage(
  msg: ChatMessage,
  threadId: string,
): PendingMessage {
  return {
    id: msg.id || `pending_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
    text: (msg.text || '').toString(),
    html: msg.html,
    attachmentsSerialized: msg.attachmentsSerialized,
    // Ingest-on-upload: keep the upload-time extraction job id with the
    // queued entry so the drained turn still carries it to the backend.
    ingestJobId: msg.ingestJobId,
    enqueuedAt: Date.now(),
    threadId,
    status: 'queued',
  };
}
